/**
 * Hydrogen Theme Utilities
 *
 * Provides theme management for light/dark mode with system preference support.
 * Designed to complete toggles in <50ms per Tao axioms.
 *
 * DOM contract (always applied — brand packs need an explicit axis):
 * - `data-theme="light"|"dark"` — Hydrogen CSS + token bridges
 * - `.dark` class when resolved dark — Tailwind-style brand packs (e.g. Venture Factory)
 *
 * The shipped stylesheet maps these authorities to `color-scheme`. Writing the
 * same value through `element.style` would violate strict `style-src-attr` CSP
 * and create a second source of truth.
 *
 * Preference `system` is stored as the preference; the document always receives
 * the *resolved* light|dark so page chrome and shells cannot diverge when a
 * brand sets light values on `:root` without a media query.
 */
import { logRuntimeWarn } from '../utils/runtime-logger.js';
const THEME_KEY = 'hydrogen-theme';
const DEFAULT_RESOLVED_THEME = 'dark';
let systemChangeUnsub = null;
function isThemePreference(theme) {
    return theme === 'light' || theme === 'dark' || theme === 'system';
}
function getStorage() {
    try {
        if (typeof globalThis !== 'undefined' && globalThis.localStorage) {
            return globalThis.localStorage;
        }
        if (typeof window !== 'undefined' && window.localStorage) {
            return window.localStorage;
        }
    }
    catch {
        return null;
    }
    return null;
}
function readStoredTheme() {
    const storage = getStorage();
    if (!storage || typeof storage.getItem !== 'function')
        return null;
    try {
        return storage.getItem(THEME_KEY);
    }
    catch {
        return null;
    }
}
function writeStoredTheme(theme) {
    const storage = getStorage();
    if (!storage || typeof storage.setItem !== 'function')
        return;
    try {
        storage.setItem(THEME_KEY, theme);
    }
    catch {
        // Storage can be unavailable in private browsing or embedded contexts.
    }
}
function getDocumentElement() {
    try {
        if (typeof document !== 'undefined' && document.documentElement) {
            return document.documentElement;
        }
        if (typeof window !== 'undefined' && window.document?.documentElement) {
            return window.document.documentElement;
        }
    }
    catch {
        return null;
    }
    return null;
}
/**
 * Apply the resolved light|dark theme to the document.
 * Preference `system` is resolved first; the DOM always gets a concrete theme.
 */
function applyResolvedTheme(resolved) {
    const element = getDocumentElement();
    if (!element) {
        return;
    }
    try {
        element.setAttribute?.('data-theme', resolved);
        if (element.classList && typeof element.classList.toggle === 'function') {
            element.classList.toggle('dark', resolved === 'dark');
        }
    }
    catch {
        // DOM access can be restricted in non-standard client runtimes.
    }
}
function getLightSchemeQuery() {
    try {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return null;
        }
        return window.matchMedia('(prefers-color-scheme: light)');
    }
    catch {
        return null;
    }
}
function resolveSystemTheme() {
    const mediaQuery = getLightSchemeQuery();
    if (!mediaQuery) {
        return DEFAULT_RESOLVED_THEME;
    }
    try {
        return mediaQuery.matches ? 'light' : 'dark';
    }
    catch {
        return DEFAULT_RESOLVED_THEME;
    }
}
/**
 * Gets the current theme setting.
 */
export function getTheme() {
    const stored = readStoredTheme();
    return isThemePreference(stored) ? stored : 'system';
}
/**
 * Gets the resolved theme (what's actually being displayed).
 * Resolves 'system' to actual light/dark based on media query.
 */
export function getResolvedTheme() {
    const theme = getTheme();
    if (theme !== 'system')
        return theme;
    return resolveSystemTheme();
}
/**
 * Sets the theme preference and applies the resolved theme to the document.
 */
export function setTheme(theme) {
    if (!isThemePreference(theme)) {
        logRuntimeWarn('theme.invalid', theme);
        return;
    }
    writeStoredTheme(theme);
    applyResolvedTheme(theme === 'system' ? resolveSystemTheme() : theme);
    ensureSystemListener();
}
/**
 * Toggles between light and dark themes.
 * If currently on system, switches to the opposite of current system preference.
 */
export function toggleTheme() {
    const resolved = getResolvedTheme();
    const newTheme = resolved === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    return newTheme;
}
/**
 * Ensure a single system-preference listener keeps the document in sync when
 * preference is `system`. Idempotent.
 */
function ensureSystemListener() {
    if (systemChangeUnsub) {
        return;
    }
    systemChangeUnsub = onSystemThemeChange(() => {
        if (getTheme() === 'system') {
            applyResolvedTheme(resolveSystemTheme());
        }
    });
}
/**
 * Initializes theme from localStorage (or system) on page load.
 * Call this in the app entry point. Always pins `data-theme` + `.dark` to the
 * resolved light|dark value so brand packs and Hydrogen shells stay coherent.
 */
export function initTheme() {
    const preference = getTheme();
    applyResolvedTheme(preference === 'system' ? resolveSystemTheme() : preference);
    ensureSystemListener();
}
/**
 * Subscribes to system theme changes.
 * @returns Unsubscribe function
 */
export function onSystemThemeChange(callback) {
    const mediaQuery = getLightSchemeQuery();
    if (!mediaQuery) {
        return () => { };
    }
    const handler = (e) => {
        callback(e.matches ? 'light' : 'dark');
    };
    if (typeof mediaQuery.addEventListener === 'function') {
        const addMediaListener = mediaQuery.addEventListener.bind(mediaQuery);
        const removeMediaListener = typeof mediaQuery.removeEventListener === 'function'
            ? mediaQuery.removeEventListener.bind(mediaQuery)
            : null;
        try {
            addMediaListener('change', handler);
        }
        catch {
            return () => { };
        }
        return () => {
            try {
                if (removeMediaListener) {
                    removeMediaListener('change', handler);
                }
            }
            catch {
                // Ignore cleanup failures from restricted media query objects.
            }
        };
    }
    // Legacy MediaQueryList API (Safari <14, older browsers)
    const legacyQuery = mediaQuery;
    if (typeof legacyQuery.addListener === 'function') {
        try {
            legacyQuery.addListener(handler);
        }
        catch {
            return () => { };
        }
        return () => {
            try {
                if (typeof legacyQuery.removeListener === 'function') {
                    legacyQuery.removeListener(handler);
                }
            }
            catch {
                // Ignore cleanup failures from restricted media query objects.
            }
        };
    }
    return () => { };
}
//# sourceMappingURL=theme.js.map